import { useEffect, useState } from 'react';
import { api, type TheaterPreferences } from '../lib/api';
import { CHARACTER_IDS, CHARACTER_META, type CharacterId } from '../theater/catalog';
import './ProfilePanel.css';

export default function ProfilePanel({
  open,
  onClose,
  onUpdated,
}: {
  open: boolean;
  onClose: () => void;
  onUpdated?: (character: string, preferences: TheaterPreferences) => void;
}) {
  const [character, setCharacter] = useState<CharacterId>('normal');
  const [prefs, setPrefs] = useState<TheaterPreferences>({
    theater_characters: true,
    thrown_effects: true,
    speech_bubbles: true,
    reactions: true,
  });
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    void api
      .theaterMe()
      .then((res) => {
        setCharacter((res.character as CharacterId) || 'normal');
        setPrefs(res.preferences);
      })
      .catch((err) => setError((err as Error).message));
  }, [open]);

  if (!open) return null;

  async function saveCharacter(id: CharacterId) {
    setError(null);
    try {
      await api.setCharacter(id);
      setCharacter(id);
      setMessage('Character updated.');
      onUpdated?.(id, prefs);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function savePref<K extends keyof TheaterPreferences>(key: K, value: boolean) {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    try {
      const res = await api.setTheaterPreferences({ [key]: value });
      setPrefs(res.preferences);
      onUpdated?.(character, res.preferences);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div className="profile-overlay" onClick={onClose}>
      <div className="profile-panel panel" onClick={(e) => e.stopPropagation()}>
        <div className="profile-head">
          <h2 className="section-title">PROFILE</h2>
          <button className="btn" onClick={onClose}>
            Close
          </button>
        </div>

        {message && <p className="muted">{message}</p>}
        {error && <div className="error-banner">{error}</div>}

        <h3>Theater character</h3>
        <div className="character-grid">
          {CHARACTER_IDS.map((id) => (
            <button
              key={id}
              type="button"
              className={`character-pick${character === id ? ' is-active' : ''}`}
              onClick={() => void saveCharacter(id)}
            >
              <span className="character-emoji">{CHARACTER_META[id].emoji}</span>
              <span>{CHARACTER_META[id].name}</span>
            </button>
          ))}
        </div>

        <h3>Preferences</h3>
        <div className="pref-list">
          {(
            [
              ['theater_characters', 'Theater characters'],
              ['thrown_effects', 'Thrown effects'],
              ['speech_bubbles', 'Speech bubbles'],
              ['reactions', 'Reactions'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="pref-row">
              <span>{label}</span>
              <select
                className="field"
                value={prefs[key] ? 'on' : 'off'}
                onChange={(e) => void savePref(key, e.target.value === 'on')}
              >
                <option value="on">ON</option>
                <option value="off">OFF</option>
              </select>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
